// import components
import { IconButton, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { WeekValueCell } from 'components/shared/RosterViewDataGrid/components/cells/WeekValueCell';
import BacklogCategoryLabel from '../BacklogCategoryLabel';
import BacklogEntityActionsMenu from '../BacklogEntityActionsMenu';
import OutsideTreatmentAppointmentCard from '../OutsideTreatmentAppointmentCard';

// import external libraries
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

// import utils
import {
  EVENT_CATEGORIES,
  extractPlanningEntity,
  getCategoryRowKey,
  getWeekValueMagnitude,
  selectWeekEvents
} from '../BacklogGrid/BacklogGrid.utils';
import {
  formatEntityDisplayLabel,
  getIconType
} from 'components/mrp/mrp.utils';

// import constants
import { backlogCategoryColor } from '../../Backlog.consts';

// import styles
import {
  Breadcrumb,
  CareReceiverBand,
  Container,
  DayCell,
  DayHeader,
  HeaderRow,
  LabelCell,
  Row,
  SectionRow
} from './BacklogWeekDetail.styles';
import { EntityIcon } from '../BacklogGrid/BacklogGrid.styles';

// import types
import type { BacklogWeekDetailProps } from './BacklogWeekDetail.types';
import type { PlanningEntityType } from 'generated/index';
import type { WeekValue } from 'components/shared/RosterViewDataGrid';

const asWeekValue = (value: unknown): WeekValue | null =>
  value && typeof value === 'object' && 'current' in value && 'total' in value
    ? (value as WeekValue)
    : null;

/**
 * The per-day view of one week, opened from an event-category cell. Per care receiver it shows the
 * appointments of each active event category (outside treatment, overlapping appointments) as cards in
 * their day column, and the week's tekort/overschot the same way the weeks grid does — a foldable
 * category with its rules — except every value sits in the Monday column, since deviations are weekly
 * rather than per-day. It honours the same tiles, filters and view settings as the weeks grid; only
 * deviation and the event categories are backed today.
 */
const BacklogWeekDetail = ({
  weekDays,
  weekField,
  backlog,
  ruleRows,
  activeCategories,
  cellValueMode,
  collapsedCategoryKeys,
  onToggleCategoryRow,
  onClose
}: BacklogWeekDetailProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  const cellDisplay =
    cellValueMode === 'plannedOverPrescribed' ? 'ratio' : 'difference';

  // The response is already scoped to the toolbar's filters server-side, so the detail only has to pick
  // the week out of it — no activity or care-receiver narrowing left to repeat here.
  const careReceivers = (backlog ?? [])
    .map((entry) => {
      const entityType = (entry.planningEntityType ??
        'CLIENT') as PlanningEntityType;
      const entity = extractPlanningEntity(entry.planningEntity, entityType);
      // One card section per active event-based category that has events this week. Both categories
      // list appointments as day cards; only their source field and label differ.
      const eventSections = EVENT_CATEGORIES.filter((category) =>
        activeCategories.includes(category)
      )
        .map((category) => ({
          category,
          events: selectWeekEvents(
            category === 'outsideTreatment'
              ? entry.outsideTreatmentEvents
              : entry.appointmentOverlapEvents,
            weekDays
          )
        }))
        .filter((section) => section.events.length > 0);
      return { entity, entityType, eventSections };
    })
    .sort((a, b) => a.entity.name.localeCompare(b.entity.name));

  return (
    <Container>
      <Breadcrumb>
        <IconButton size="small" onClick={onClose} aria-label={t('backToList')}>
          <Icon type="chevron-left" sx={{ width: 16 }} color="action" />
        </IconButton>
        {/* Names where the chevron goes rather than the week already on screen: the week is spelled out
            by the day headers underneath, so repeating it here spent the one line that could tell a
            planner how to get back. */}
        <Typography variant="body1">{t('backToEightWeeks')}</Typography>
      </Breadcrumb>

      <HeaderRow>
        <LabelCell />
        {weekDays.map((day) => (
          <DayHeader key={day.date.toISOString()}>
            <Typography variant="body2">{day.dayName}</Typography>
            <Typography variant="caption">
              {dayjs(day.date).format('DD-MM')}
            </Typography>
          </DayHeader>
        ))}
      </HeaderRow>

      {careReceivers.map(({ entity, entityType, eventSections }) => {
        const deviationRules = ruleRows.filter(
          (row) =>
            row.category === 'deviation' &&
            row.entityId === entity.id &&
            asWeekValue(row[weekField]) !== null
        );

        // A care receiver with nothing to report this week is left out entirely.
        if (!deviationRules.length && !eventSections.length) return null;

        const deviationCollapsed = collapsedCategoryKeys.has(
          getCategoryRowKey(entity.id, 'deviation')
        );
        // The category's own value is the magnitude of its rules that week — the same figure the weeks
        // grid shows on the category row, here landing on Monday.
        const deviationOutstanding = deviationRules.reduce(
          (sum, row) =>
            sum + getWeekValueMagnitude(asWeekValue(row[weekField])),
          0
        );

        return (
          <div key={entity.id}>
            <CareReceiverBand>
              {/* The planning-entity symbol — client, employee, resource or workplace — the same one
                  the detail tooltip shows before the patients: glyph via getIconType, colour via entityType. */}
              <EntityIcon
                type={getIconType(entityType)}
                entityType={entityType}
              />
              {formatEntityDisplayLabel(entity.name, entity.number)}
              {/* The same menu the weeks grid puts on its entity rows — a planner who found it there
                  should find it here, on the same band. */}
              <BacklogEntityActionsMenu
                entityId={entity.id}
                entityName={entity.name}
                entityType={entityType}
              />
            </CareReceiverBand>

            {deviationRules.length > 0 && (
              <>
                <SectionRow>
                  <LabelCell>
                    <BacklogCategoryLabel
                      label={t('categories.deviation')}
                      count={deviationRules.length}
                      fold={{
                        isExpanded: !deviationCollapsed,
                        onToggle: () =>
                          onToggleCategoryRow(entity.id, 'deviation'),
                        ariaLabel: t('toggleCategory', {
                          name: t('categories.deviation')
                        })
                      }}
                    />
                  </LabelCell>
                  {weekDays.map((day, dayIndex) => (
                    <DayCell key={day.date.toISOString()}>
                      {dayIndex === 0 && (
                        <WeekValueCell value={deviationOutstanding} isSummary />
                      )}
                    </DayCell>
                  ))}
                </SectionRow>

                {!deviationCollapsed &&
                  deviationRules.map((row) => (
                    <Row key={row.id}>
                      <LabelCell indented>
                        <Typography variant="body2" noWrap>
                          {row.activityName}
                        </Typography>
                      </LabelCell>
                      {weekDays.map((day, dayIndex) => (
                        <DayCell key={day.date.toISOString()}>
                          {dayIndex === 0 && (
                            <WeekValueCell
                              value={asWeekValue(row[weekField])}
                              display={cellDisplay}
                            />
                          )}
                        </DayCell>
                      ))}
                    </Row>
                  ))}
              </>
            )}

            {eventSections.map(({ category, events }) => (
              <SectionRow key={category}>
                <LabelCell>
                  <BacklogCategoryLabel
                    label={t(`categories.${category}`)}
                    count={events.length}
                  />
                </LabelCell>
                {weekDays.map((day) => {
                  const dayEvents = events.filter((event) =>
                    dayjs(event.date).isSame(day.date, 'day')
                  );
                  return (
                    <DayCell key={day.date.toISOString()}>
                      {dayEvents.map((event, index) => (
                        <OutsideTreatmentAppointmentCard
                          key={`${event.id}-${index}`}
                          event={event}
                          careReceiverId={entity.id}
                          entityType={entityType}
                          accentColor={backlogCategoryColor(category)}
                        />
                      ))}
                    </DayCell>
                  );
                })}
              </SectionRow>
            ))}
          </div>
        );
      })}
    </Container>
  );
};

export default BacklogWeekDetail;
